import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { isWafConfigured } from "config/authApi";
import { loadAwsWafScript, renderAwsWafCaptcha } from "services/awsWaf";

function AwsWafCaptcha({ onToken, onError, resetKey = 0, enabled = true }) {
  const containerRef = useRef(null);
  const onTokenRef = useRef(onToken);
  const onErrorRef = useRef(onError);
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    onTokenRef.current = onToken;
  }, [onToken]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!enabled) {
      setStatus("idle");
      setErrorMessage(null);
      onTokenRef.current?.(null);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    if (!isWafConfigured()) {
      setStatus("disabled");
      onTokenRef.current?.(null);
      return undefined;
    }

    let cancelled = false;

    const mountCaptcha = async () => {
      setStatus("loading");
      setErrorMessage(null);

      try {
        await loadAwsWafScript();
        if (cancelled || !containerRef.current) {
          return;
        }

        await renderAwsWafCaptcha(containerRef.current, {
          onSuccess: (wafToken) => {
            if (cancelled) {
              return;
            }
            setStatus("ready");
            setErrorMessage(null);
            onTokenRef.current?.(wafToken);
          },
          onError: (error) => {
            if (cancelled) {
              return;
            }
            const message =
              error?.message || "CAPTCHA verification failed. Please try again.";
            setStatus("error");
            setErrorMessage(message);
            onErrorRef.current?.(error);
            onTokenRef.current?.(null);
          },
        });

        if (!cancelled) {
          setStatus("pending");
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        const message =
          error?.message || "Unable to load CAPTCHA. Please refresh and try again.";
        setStatus("error");
        setErrorMessage(message);
        onErrorRef.current?.(error);
        onTokenRef.current?.(null);
      }
    };

    mountCaptcha();

    return () => {
      cancelled = true;
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
    };
  }, [resetKey, enabled]);

  if (!isWafConfigured()) {
    return (
      <SoftBox mb={2} p={2} borderRadius="lg" bgColor="grey-100">
        <SoftTypography variant="caption" color="secondary">
          CAPTCHA is not configured for this environment. Auth requests will use
          the direct API endpoint.
        </SoftTypography>
      </SoftBox>
    );
  }

  if (!enabled) {
    return null;
  }

  if (status === "ready") {
    return (
      <SoftBox mb={2} p={2} borderRadius="lg" bgColor="success">
        <SoftTypography variant="caption" color="white" fontWeight="medium">
          Security check complete. You can continue.
        </SoftTypography>
      </SoftBox>
    );
  }

  return (
    <SoftBox mb={2}>
      <SoftTypography variant="caption" color="text" fontWeight="medium" mb={1}>
        Complete the security check below
      </SoftTypography>
      <SoftBox
        ref={containerRef}
        id="aws-waf-captcha-container"
        sx={{
          minHeight: status === "loading" ? 120 : 0,
          display: "flex",
          justifyContent: "center",
        }}
      />
      {status === "loading" && (
        <SoftTypography variant="caption" color="secondary" mt={1}>
          Loading security check...
        </SoftTypography>
      )}
      {errorMessage && (
        <SoftBox mt={1}>
          <SoftTypography variant="caption" color="error">
            {errorMessage}
          </SoftTypography>
        </SoftBox>
      )}
    </SoftBox>
  );
}

AwsWafCaptcha.propTypes = {
  onToken: PropTypes.func,
  onError: PropTypes.func,
  resetKey: PropTypes.number,
  enabled: PropTypes.bool,
};

export default AwsWafCaptcha;
